import { Box, Chip, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { useTranslation } from 'react-i18next';
import { round } from '@kheops/utils';
import MinimumOrderValueChip from '../../common/components/MinimumOrderValueChip';
import PriceModeChip from '../../common/components/PriceModeChip';
import VersionedValue from '../../common/components/VersionedValue';
import ContractsUtils from '../../common/utils/contracts.utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { currentContextAtom } from '../../state';
import { orderAtom, viewOrderItemsAtom } from '../state/state';
import CommissionInfo from '../../common/components/CommissionInfo';
import PackageIcon from '../../assets/icons/package.svg?react';

export default function OrderChipsInfo(): React.JSX.Element {
  const { t } = useTranslation(['contracts', 'common', 'price']);
  const { realm } = useAtomValue(currentContextAtom);
  const order = useAtomValue(orderAtom);
  const items = useAtomValue(viewOrderItemsAtom);
  const currencyFormat = useCurrencyFormat();

  const refreshedTotal = round(
    items.reduce((acc, packagingQt) => ( // we round the total since now packaging quantities prices support 3 decimal values
      acc + (packagingQt.price)
    ), 0),
  );
  const totalPackagingsNumber = items.reduce((acc, item) => acc + item.quantity, 0);
  const currentTotal = order.total_price_excl_tax!;
  const previousTotal = order.previousOrders[0]?.total_price_excl_tax;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: {
          xs: 'wrap',
          sm: 'nowrap',
        },
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="bodySmall">
          {t('price:price_without_tax_total_ordered')}
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            minHeight: 24,
            alignItems: 'center',
            ...(realm === Business_Profile_Enum.Supplier && {
              minHeight: 40,
              mt: 0,
            }),
          }}
        >
          <VersionedValue
            currentValue={refreshedTotal}
            previousValue={previousTotal || currentTotal}
            color="primary.main"
            valueFormatter={currencyFormat.format}
            shouldHighlightDifference
            variant="titleMedium"
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="bodySmall">
          {t('packaging:case')}
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            minHeight: 24,
            alignItems: 'center',
            ...(realm === Business_Profile_Enum.Supplier && {
              minHeight: 40,
              mt: 0,
            }),
          }}
        >
          <Chip
            variant="tag"
            icon={<PackageIcon />}
            label={totalPackagingsNumber}
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="bodySmall">
          {t('common:minimum_order_value')}
        </Typography>
        <MinimumOrderValueChip
          sx={{
            mt: 0.5,
            ...(realm === Business_Profile_Enum.Supplier && {
              minHeight: 40,
              display: 'flex',
              alignItems: 'center',
              mt: 0,
            }),
          }}
          minimumOrderValue={order.contract.minimum_order_value as number}
          minimumOrderValueType={order.contract.minimum_order_value_type}
          disableTooltip
        />
      </Box>
      {ContractsUtils.displayPriceMode(realm, order.contract) && (
        <Box
          sx={{
            display: {
              xs: 'none',
              lg: 'block',
            },
          }}
        >
          <Typography variant="bodySmall">
            {t('contracts:discount')}
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              ...(realm === Business_Profile_Enum.Supplier && {
                minHeight: 40,
                display: 'flex',
                alignItems: 'center',
                mt: 0,
              }),
            }}
          >
            <PriceModeChip
             contract={order.contract} 
             variant="tag"
            />
          </Box>
        </Box>
      )}
      {realm === Business_Profile_Enum.Supplier && (
        <Box
          sx={{
            display: {
              xs: 'none',
              lg: 'block',
            },
          }}
        >
          <Typography
            variant="bodySmall"
          >
            {t('contracts:commission')}
          </Typography>
          <CommissionInfo
            short
            commission={order.contract.supplier_commission_rate as number}
          />
        </Box>
      )}
    </Box>
  );
}
